import React, { useEffect, useState } from 'react';
import './Notification.css';
import { useNavigate } from 'react-router-dom';
import { useApiServices } from '../../services/endpoints/ApiEndpoint';
import { getLocalStorage } from '../../utils/helper';

// ─── Types ────────────────────────────────────────────────────────────────────

type NotificationType = 'TRANSACTIONS' | 'NEWS' | 'SYSTEM';
type TabKey = 'ALL' | NotificationType;

interface AppNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  userId: string;
  transactionId: string | null;
  iconUrl: string | null;
  actionUrl: string | null;
  isRead: boolean;
  readAt: string | null;
  priority: string;
  metadata: any;
  createDate: string;
  transactionNumber: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toLocalDate(dateStr: string): Date {
  // API returns UTC timestamps without 'Z' suffix — append it so the browser parses as UTC
  const normalized = dateStr.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateStr)
    ? dateStr
    : dateStr + 'Z';
  return new Date(normalized);
}

function relativeTime(dateStr: string): string {
  const date = toLocalDate(dateStr);
  const diff = Date.now() - date.getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return 'เมื่อสักครู่';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} นาทีที่แล้ว`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} ชั่วโมงที่แล้ว`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} วันที่แล้ว`;
  return date.toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' });
}

const TYPE_ICON: Record<string, string> = {
  TRANSACTIONS: '/assets/icons/icon-vehicle-truck-cube-orange.svg',
  NEWS: '/assets/icons/icon-bell.svg',
  SYSTEM: '/assets/icons/icon-setting.svg',
};

const TABS: { key: TabKey; label: string }[] = [
  { key: 'ALL', label: 'ทั้งหมด' },
  { key: 'TRANSACTIONS', label: 'การขนส่ง' },
  { key: 'NEWS', label: 'ข่าวสาร' },
  { key: 'SYSTEM', label: 'ระบบ' },
];

// ─── Component ────────────────────────────────────────────────────────────────

const Notification: React.FC = () => {
  const { GetNotificationsAsync, MarkNotificationReadAsync, MarkAllNotificationsReadAsync, DeleteNotificationAsync } = useApiServices();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [activeTab, setActiveTab] = useState<TabKey>('ALL');
  const [isLoading, setIsLoading] = useState(true);

  // ── Fetch ────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const token = getLocalStorage('accessToken');
    if (!token) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    GetNotificationsAsync()
      .then(res => {
        if (res?.isSuccess && Array.isArray(res.result?.items)) {
          setNotifications(res.result.items as AppNotification[]);
        }
      })
      .catch(() => {/* keep empty list */})
      .finally(() => setIsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Actions ──────────────────────────────────────────────────────────────────

  const handleCardClick = async (notif: AppNotification) => {
    // Mark as read (fire-and-forget, optimistic)
    if (!notif.isRead) {
      setNotifications(prev =>
        prev.map(n => (n.id === notif.id ? { ...n, isRead: true } : n))
      );
      MarkNotificationReadAsync([notif.id]).catch(() => {
        setNotifications(prev =>
          prev.map(n => (n.id === notif.id ? { ...n, isRead: false } : n))
        );
      });
    }
    // Navigate to actionUrl if available
    if (notif.actionUrl) {
      navigate(notif.actionUrl);
    }
  };

  const handleMarkAllRead = async () => {
    const prevState = notifications;
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    try {
      await MarkAllNotificationsReadAsync();
    } catch {
      setNotifications(prevState);
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const prevState = notifications;
    setNotifications(prev => prev.filter(n => n.id !== id));
    try {
      await DeleteNotificationAsync(id);
    } catch {
      setNotifications(prevState);
    }
  };

  // ── Derived data ─────────────────────────────────────────────────────────────

  const filtered = activeTab === 'ALL'
    ? notifications
    : notifications.filter(n => n.type === activeTab);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <section className="nt-wrapper">
      <div className="nt-container lg:container lg:mx-auto">

        {/* Header row */}
        <div className="nt-header">
          <h1 className="nt-title">
            การแจ้งเตือน
            {unreadCount > 0 && (
              <span className="nt-unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </h1>
          {unreadCount > 0 && (
            <button className="nt-mark-all-btn" onClick={handleMarkAllRead}>
              อ่านทั้งหมด
            </button>
          )}
        </div>

        {/* Tab bar */}
        <div className="nt-tabs" role="tablist">
          {TABS.map(tab => {
            const tabUnread = tab.key === 'ALL'
              ? unreadCount
              : notifications.filter(n => n.type === tab.key && !n.isRead).length;
            return (
              <button
                key={tab.key}
                role="tab"
                aria-selected={activeTab === tab.key}
                className={`nt-tab${activeTab === tab.key ? ' nt-tab--active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                {tabUnread > 0 && (
                  <span className="nt-tab-dot" />
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="nt-loading">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="nt-skeleton">
                <div className="nt-skeleton-icon" />
                <div className="nt-skeleton-body">
                  <div className="nt-skeleton-line nt-skeleton-line--title" />
                  <div className="nt-skeleton-line nt-skeleton-line--sub" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState tab={activeTab} />
        ) : (
          <div className="nt-list">
            {filtered.map(notif => (
              <NotificationCard
                key={notif.id}
                notif={notif}
                onRead={handleCardClick}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// ─── Sub-components ──────────────────────────────────────────────────────────

interface NotificationCardProps {
  notif: AppNotification;
  onRead: (n: AppNotification) => Promise<void>;
  onDelete: (id: string, e: React.MouseEvent) => void;
}

const NotificationCard: React.FC<NotificationCardProps> = ({ notif, onRead, onDelete }) => {
  const icon = notif.iconUrl || TYPE_ICON[notif.type] || '/assets/icons/icon-bell.svg';

  return (
    <div
      className={`nt-card${notif.isRead ? '' : ' nt-card--unread'}`}
      onClick={() => onRead(notif)}
      role="article"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onRead(notif)}
      aria-label={notif.title}
    >
      {/* Unread indicator */}
      {!notif.isRead && <span className="nt-card-dot" aria-label="ยังไม่ได้อ่าน" />}

      {/* Icon */}
      <div className="nt-card-icon-wrap">
        <img className="nt-card-icon" src={icon} alt={notif.type} />
      </div>

      {/* Body */}
      <div className="nt-card-body">
        {notif.type === 'TRANSACTIONS' && notif.transactionNumber && (
          <div className="nt-card-meta">
            <span className="nt-order-number">#{notif.transactionNumber}</span>
          </div>
        )}
        <p className="nt-card-title">{notif.title}</p>
        {notif.message && <p className="nt-card-desc">{notif.message}</p>}
        <p className="nt-card-time">{relativeTime(notif.createDate)}</p>
      </div>

      {/* Delete button */}
      <button
        className="nt-delete-btn"
        aria-label="ลบการแจ้งเตือน"
        onClick={e => onDelete(notif.id, e)}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};

function EmptyState({ tab }: { tab: TabKey }) {
  const label = tab === 'ALL' ? 'การแจ้งเตือน' : (TABS.find(t => t.key === tab)?.label ?? 'การแจ้งเตือน');
  return (
    <div className="nt-empty">
      <svg className="nt-empty-icon" viewBox="0 0 64 64" fill="none">
        <circle cx="32" cy="32" r="30" stroke="#E5E7EB" strokeWidth="2" />
        <path d="M32 14c-8.837 0-16 7.163-16 16 0 5.657 2.948 10.624 7.374 13.467L22 46h20l-1.374-2.533C45.052 40.624 48 35.657 48 30c0-8.837-7.163-16-16-16z" stroke="#D1D5DB" strokeWidth="2" strokeLinejoin="round" />
        <path d="M28 46v1a4 4 0 008 0v-1" stroke="#D1D5DB" strokeWidth="2" strokeLinecap="round" />
        <line x1="32" y1="22" x2="32" y2="32" stroke="#D1D5DB" strokeWidth="2" strokeLinecap="round" />
        <circle cx="32" cy="36" r="1.5" fill="#D1D5DB" />
      </svg>
      <p className="nt-empty-title">ไม่มี{label}</p>
      <p className="nt-empty-sub">คุณจะได้รับการแจ้งเตือนที่นี่เมื่อมีความเคลื่อนไหว</p>
    </div>
  );
}

export default Notification;
